import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  einstellungen as ladeEinstellungen,
  einstellungSetzen,
  rollen as ladeRollen,
  type Einstellung,
  type Rolle,
} from '../api/verwaltung';
import { ApiFehler } from '../api/fehler';
import { Einstellungsseite, Thema } from '../komponenten/Einstellungen';
import { Meldung } from '../komponenten/Meldung';
import { Kennwortrichtlinie } from './Kennwortrichtlinie';
import { Kostentraegerarten } from './Kostentraegerarten';

/**
 * Die Einstellungen des Hauses (TAG-25, Tabelle 10).
 *
 * ============================================================================
 * WARUM ES DIESE MASKE GEBEN MUSS
 * ============================================================================
 *
 * Tabelle 10 weist „Planungsregeln vorhandener Art: Betreuungsschlüssel,
 * Betriebsmittelverhältnis, Pausen, Mindestdauer, Wegezeiten" als Sache der
 * ADMINISTRATION aus, und TAG-25 sagt: „durch die Administration pflegbar".
 *
 * Gepflegt wurden sie bisher von Migrationen. Mehrere Beschreibungen in
 * `sys.einstellung` versprachen „in der Verwaltung änderbar" — und es gab
 * keine Verwaltung dafür. Eine Einstellung, die nur per SQL zu ändern ist,
 * ist Programmierung (BUC-45 sagt denselben Satz über die Staffeln).
 *
 * ============================================================================
 * DER SCHLÜSSEL IST KEIN FELD
 * ============================================================================
 *
 * Einen neuen Schlüssel anzulegen hieße, einen Wert zu speichern, den kein
 * Programmteil liest. Das wäre eine Behauptung und keine Einstellung. Neue
 * Schlüssel kommen mit dem Code, der sie braucht; hier werden Werte geändert.
 *
 * ============================================================================
 * DIE GRUPPEN SIND DER TEIL VOR DEM PUNKT
 * ============================================================================
 *
 * `planung.mindestpauseBloecke`, `ressourcen.besetzungsfristTage`: Der
 * Schlüssel trägt seine Gruppe selbst. Eine gepflegte Zuordnung daneben
 * liefe beim ersten neuen Schlüssel auseinander. Nur der NAME der Gruppe
 * steht hier; eine Gruppe ohne Namen erscheint mit großem Anfangsbuchstaben
 * und nicht als Kleinwort.
 *
 * ============================================================================
 * DIE ZEILE HEISST, WIE SIE IN DER DATENBANK HEISST (CRM-76)
 * ============================================================================
 *
 * Gezeigt wird die Bezeichnung aus `sys.einstellung` (0294) — „Mindestpause
 * (Blöcke)" und nicht `planung.mindestpauseBloecke`. Sie kommt mit der Zeile
 * und nicht aus einer Liste hier, aus demselben Grund wie oben: Ein neuer
 * Schlüssel bringt seinen Namen in seiner Migration mit. Fehlt er doch, steht
 * ersatzweise der Schlüssel da — eine Zeile ohne Namen wäre schlechter. Der
 * Schlüssel bleibt als Tooltip, für die Rückfrage beim Support.
 *
 * ============================================================================
 * EINE ROLLE WIRD GEWÄHLT, NICHT GETIPPT (CRM-79)
 * ============================================================================
 *
 * `absage.eskalationRolle`, `ressourcen.eskalationRolle`: Der Wert ist die
 * Kennung einer Rolle. Ein Textfeld zeigte `disposition` und verlangte, die
 * Kennung zu kennen. Jeder Schlüssel auf `Rolle` bekommt deshalb eine Auswahl
 * mit den Bezeichnungen; gespeichert wird weiter die Kennung. Steht eine
 * Kennung im Wert, die keine aktive Rolle ist, bleibt sie als eigene Zeile
 * stehen und sagt das — sonst zeigte die Auswahl stillschweigend etwas anderes,
 * als gilt.
 *
 * ============================================================================
 * SEITENTYP EINSTELLUNGEN (ÄA-41): JE GRUPPE EINE KARTE — MIT TABELLE
 * ============================================================================
 *
 * Je Gruppe ein Thema über die ganze Breite. Darin bleibt die Tabelle und
 * wird KEIN Feldraster mit Stift: Die Feldzeile zeigt ihre Hilfe erst beim
 * Bearbeiten, und die Bedeutung ist hier die einzige Erklärung einer
 * Einstellung wie „Mindestpause (Blöcke)" — sie muss lesbar sein, bevor
 * jemand den Wert anfasst. Dazu gehört der Stand „geändert am", den die
 * Feldzeile nicht kennt.
 */

const GRUPPENNAME: Readonly<Record<string, string>> = {
  planung: 'Planungsregeln', // TAG-17, TAG-18
  ressourcen: 'Ressourcen und Besetzung',
  rechtstexte: 'Rechtstexte',
  // 0254: Kopfzeile oben links und der Titel im Browserreiter.
  oberflaeche: 'Kopfzeile und Fenstertitel',
  absage: 'Absagen', // BUC-129, BUC-132
  angebote: 'Terminoptionen',
  betriebspruefung: 'Festschreibung', // BP-12
  buchung: 'Buchungsstrecke', // BUC-07
  checkin: 'Check-in', // TAG-49
  dienstleister: 'Dienstleister',
};

/** `planung.mindestpauseBloecke` → `planung` */
function gruppe(schluessel: string): string {
  return schluessel.split('.')[0] ?? schluessel;
}

/** Eine Gruppe ohne gepflegten Namen beginnt wenigstens groß. */
function gruppenname(g: string): string {
  return GRUPPENNAME[g] ?? g.charAt(0).toUpperCase() + g.slice(1);
}

/** Der Wert ist die Kennung einer Rolle (CRM-79). */
function istRollenwahl(z: Einstellung): boolean {
  return z.schluessel.endsWith('Rolle');
}

/** Die Bezeichnung aus der Datenbank (0294); ohne sie der Schlüssel. */
function name(z: Einstellung): string {
  return z.bezeichnung ?? z.schluessel;
}

/** Ein Zeitpunkt als `TT.MM.JJJJ HH:MM`. */
function zeitpunkt(wert: string): string {
  const d = new Date(wert);
  return Number.isNaN(d.getTime())
    ? wert
    : d.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

export function Einstellungen({
  darfKonfigurieren,
  darfKostentraegerartenSehen = false,
}: {
  readonly darfKonfigurieren: boolean;
  /** E-74 (CRM-163): Die Werteliste lesen verlangt bgzuschuss/sehen, wie die API. */
  readonly darfKostentraegerartenSehen?: boolean;
}): JSX.Element {
  const [zeilen, setzeZeilen] = useState<readonly Einstellung[]>([]);
  /** Der Entwurf je Schlüssel — gespeichert wird je Zeile, nicht als Ganzes. */
  const [entwurf, setzeEntwurf] = useState<Readonly<Record<string, string>>>({});
  const [rollen, setzeRollen] = useState<readonly Rolle[]>([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);

  const holen = useCallback(async () => {
    setzeLaedt(true);
    try {
      const liste = await ladeEinstellungen();
      setzeZeilen(liste);
      setzeEntwurf(Object.fromEntries(liste.map((e) => [e.schluessel, e.wert])));
      if (liste.some(istRollenwahl)) setzeRollen(await ladeRollen());
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    } finally {
      setzeLaedt(false);
    }
  }, []);

  useEffect(() => {
    void holen();
  }, [holen]);

  /*
   * JE ZEILE EIN SPEICHERN. Die Einstellungen haben nichts miteinander zu
   * tun; wer die Pausenregel ändert, will nicht zugleich die Anfragefrist
   * abschicken. Ein Speicherknopf über allem wäre die Frage „gilt das jetzt
   * schon?" bei jeder Zeile.
   */
  async function speichern(z: Einstellung): Promise<void> {
    setzeMeldung(null);
    try {
      await einstellungSetzen(z.schluessel, entwurf[z.schluessel] ?? '');
      await holen();
      setzeMeldung(`${name(z)} gespeichert.`);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }

  const gruppen = [...new Set(zeilen.map((z) => gruppe(z.schluessel)))];

  return (
    <div className="stapel">
      <p className="hilfe">
        Die Regelwerte des Hauses — Pausenregeln, Fristen, Vorläufe. Sie sind hier änderbar,
        ohne dass etwas programmiert werden muss. Ein geänderter Wert gilt
        beim nächsten Lesen; bereits gerechnete Pläne werden nicht rückwirkend neu bewertet.
      </p>

      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}
      {laedt && <p className="hinweiszeile">Wird geladen …</p>}

      <Einstellungsseite>
        {gruppen.map((g) => (
          <Thema key={g} titel={gruppenname(g)} breit>
            <div className="tabellenhuelle tabellenhuelle--telefon">
              <table className="liste__tabelle" aria-label={gruppenname(g)}>
                <thead>
                  <tr>
                    <th scope="col">Einstellung</th>
                    <th scope="col">Wert</th>
                    <th scope="col">Bedeutung</th>
                    {darfKonfigurieren && <th scope="col">Handlung</th>}
                  </tr>
                </thead>
                <tbody>
                  {zeilen
                    .filter((z) => gruppe(z.schluessel) === g)
                    .map((z) => (
                      <tr key={z.schluessel}>
                        <th scope="row" title={z.schluessel}>
                          {z.bezeichnung ?? <code>{z.schluessel}</code>}
                          <br />
                          <span className="hilfe">geändert {zeitpunkt(z.geaendertAm)}</span>
                        </th>
                        <td>
                          {istRollenwahl(z) ? (
                            <select
                              className="feld__eingabe"
                              aria-label={name(z)}
                              value={entwurf[z.schluessel] ?? ''}
                              disabled={!darfKonfigurieren}
                              onChange={(e) => {
                                setzeEntwurf({ ...entwurf, [z.schluessel]: e.target.value });
                              }}
                            >
                              {!rollen.some((r) => r.kennung === z.wert) && (
                                <option value={z.wert}>{z.wert} (keine aktive Rolle)</option>
                              )}
                              {rollen.map((r) => (
                                <option key={r.kennung} value={r.kennung}>
                                  {r.bezeichnung}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <input
                              className="feld__eingabe feld__eingabe--schmal"
                              aria-label={name(z)}
                              value={entwurf[z.schluessel] ?? ''}
                              disabled={!darfKonfigurieren}
                              onChange={(e) => {
                                setzeEntwurf({ ...entwurf, [z.schluessel]: e.target.value });
                              }}
                            />
                          )}
                        </td>
                        <td>
                          {/*
                            Die Beschreibung steht IN der Tabelle und nicht hinter
                            einem Fragezeichen: Sie ist die einzige Erklärung, die
                            es gibt, und wer den Wert ändert, soll sie gelesen haben.
                          */}
                          <span className="hilfe">{z.beschreibung ?? '—'}</span>
                        </td>
                        {darfKonfigurieren && (
                          <td>
                            <button
                              type="button"
                              className="knopf knopf--klein"
                              aria-label={`${name(z)} speichern`}
                              disabled={(entwurf[z.schluessel] ?? '') === z.wert}
                              onClick={() => void speichern(z)}
                            >
                              Speichern
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </Thema>
        ))}
        {/* AeA-48: der Kennwortablauf, eigene Quelle sys.anmelderichtlinie. */}
        <Kennwortrichtlinie darfKonfigurieren={darfKonfigurieren} />
        {/* E-74, CRM-163: die Werteliste „Art des Kostenträgers". */}
        {darfKostentraegerartenSehen && <Kostentraegerarten darfKonfigurieren={darfKonfigurieren} />}
      </Einstellungsseite>
    </div>
  );
}
